"use client";

import { useQuery } from "@apollo/client/react";
import type { PaginationState } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import TrendingCoinsTable from "@/components/app/trade/trending-coins/trending-coins-table";
import TablePagination from "@/components/shared/table-pagination";
import UserMultipleSelect from "@/components/shared/users-search/user-multiple-select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FilterType, useFiltersAndSort } from "@/contexts/filters-sort-context";
import type { TradingTopTradersByTokenByVolume } from "@/graphql/codegen/graphql";
import {
  TradingTopTradersByTokenByVolumesDocument,
  TradingTopTradersByTokenByVolumesOrderBy,
} from "@/graphql/codegen/graphql";
import { APP_NAME_GENERIC } from "@/utils/constants";
import { getOrderByKey } from "@/utils/trade";
import type { PartialAccount } from "@/utils/types";

import type { Filters } from "../utils";
import { formatFilters } from "../utils";
import { FilterName, TopTraders } from "./constants";
import {
  COLUMNS_WIDTH,
  TOP_TRADERS_TABS,
  TopTradersFilterFields,
} from "./constants";
import { TradeTopTradersFilterPopover } from "./trade-top-traders-filter-popover";
import { columns as tableColumns } from "./trade-top-traders-table-columns";

export const INITIAL_FILTERS_AND_SORTS = {
  initialFilters: [],
  initialSort: null,
};

type SortingState = {
  id: TradingTopTradersByTokenByVolumesOrderBy;
  desc: boolean;
};

const TradeTopTradersTableWrapper = () => {
  const [activeTab, setActiveTab] = useState(TOP_TRADERS_TABS[0]);
  const [pageOffset, setPageOffset] = useState(0);
  const [itemsPerPage] = useState(25);
  const [columnSorting, setColumnSorting] = useState<SortingState[]>([
    {
      id: TradingTopTradersByTokenByVolumesOrderBy.TotalVolumeUsdDesc,
      desc: true,
    },
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: itemsPerPage,
  });
  const {
    setFilters,
    findFilterByName,
    removeFilter,
    filters,
    isFilterActive,
    updateFilter,
    syncLastSavedFilters,
    setFiltersToLastSaved,
  } = useFiltersAndSort(INITIAL_FILTERS_AND_SORTS);

  const traders =
    (findFilterByName(TopTradersFilterFields.traderPublicKey, true)
      ?.value as PartialAccount[]) ?? [];

  const setTraders = (users: PartialAccount[]) => {
    updateFilter(
      {
        name: TopTradersFilterFields.traderPublicKey,
        type: FilterType.multiUserSelect,
        value: users,
      },
      false,
      true,
    );
  };

  const methods = {
    findFilterByName,
    updateFilter,
    removeFilter,
    isFilterActive,
    syncLastSavedFilters,
    setFiltersToLastSaved,
  };

  const topTraderQueryVariables = useMemo(() => {
    const formatFiltersParams =
      Object.keys(filters).length > 0 ? formatFilters(filters) : {};

    const filter: Filters = {
      tokenPublicKey: {
        equalTo: "*",
      },
      timeWindow: {
        equalTo: TopTraders.ALL_TIME,
      },
      ...formatFiltersParams,
    };

    return {
      first: itemsPerPage,
      offset: pageOffset,
      orderBy: getOrderByKey(
        columnSorting,
        TradingTopTradersByTokenByVolumesOrderBy,
        TradingTopTradersByTokenByVolumesOrderBy.TotalVolumeUsdDesc,
      ),
      filter,
    };
  }, [columnSorting, filters, itemsPerPage, pageOffset]);

  const {
    data: topTradersData,
    loading: loadingActivityCoins,
    refetch: refetchTopTraders,
  } = useQuery(TradingTopTradersByTokenByVolumesDocument, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: topTraderQueryVariables,
  });

  const columns = useMemo(() => tableColumns(), []);

  const topTraders =
    (topTradersData?.tradingTopTradersByTokenByVolumes?.nodes?.filter(
      Boolean,
    ) as TradingTopTradersByTokenByVolume[]) ?? [];
  const totalCount =
    topTradersData?.tradingTopTradersByTokenByVolumes?.totalCount ?? 0;
  const isFirstLoad = loadingActivityCoins && !topTradersData;

  const resetPagination = () => {
    setPagination({
      pageIndex: 0,
      pageSize: itemsPerPage,
    });
    setPageOffset(0);
  };

  const handleActiveTab = (tab: TopTraders) => {
    setActiveTab(tab);

    if (tab === TopTraders.ALL_TIME) {
      removeFilter(FilterName.TimeWindow);
    } else {
      updateFilter({
        name: FilterName.TimeWindow,
        type: FilterType.select,
        value: tab,
      });
    }

    resetPagination();
  };

  const handleResetFilters = () => {
    setActiveTab(TOP_TRADERS_TABS[0]);
    setFilters(INITIAL_FILTERS_AND_SORTS.initialFilters, true);
    setTraders([]);
    resetPagination();
  };

  const handleApplyFilters = () => {
    resetPagination();
  };

  const handleSelectedUsers = (users: PartialAccount[]) => {
    if (!users.length) {
      removeFilter(TopTradersFilterFields.traderPublicKey, true);
    } else {
      updateFilter(
        {
          name: TopTradersFilterFields.traderPublicKey,
          type: FilterType.multiUserSelect,
          value: users,
        },
        false,
        true,
      );
    }

    resetPagination();
  };

  const handleColumnSortingChange = (sorting: SortingState[]) => {
    setColumnSorting(sorting);
    resetPagination();
  };

  const onPaginate = (nextPageIndex: number) => {
    const offset = nextPageIndex * itemsPerPage;

    setPagination({
      pageIndex: nextPageIndex,
      pageSize: itemsPerPage,
    });
    setPageOffset(offset);
  };

  const refetchWithUpdatedParams = async () => {
    await refetchTopTraders();
  };

  return isFirstLoad ? (
    <div className="flex flex-col">
      <div
        className="
          flex w-full flex-col items-start justify-normal gap-2
          xl:flex-row xl:items-end xl:justify-between
        "
      >
        <Skeleton
          className="
            mb-4 h-[38px] w-full
            xl:mb-0 xl:w-[250px]
          "
        />
        <Skeleton
          className="
            h-[38px] w-full
            xl:w-[535px]
          "
        />
      </div>
      <Skeleton className="mt-4 h-[620px] w-full" />
    </div>
  ) : (
    <div className="min-h-[700px]">
      <div
        className="
          mb-4 flex flex-col items-start justify-between gap-2
          xl:flex-row xl:items-center
        "
      >
        <div>
          <h2 className="font-sans text-xl font-semibold text-muted-foreground">
            Top Traders
          </h2>
          <p className="text-muted">
            View all top traders on {APP_NAME_GENERIC}
          </p>
        </div>

        <div
          className="
            flex w-full flex-col justify-between gap-6
            sm:flex-row sm:items-center sm:gap-2
            md:w-auto md:justify-normal
          "
        >
          <div
            className="
              w-full overflow-x-auto
              md:w-auto
            "
          >
            <div className="flex min-w-[315px] gap-x-1.5">
              {TOP_TRADERS_TABS.map((tab) => (
                <Button
                  key={tab}
                  onClick={() => handleActiveTab(tab)}
                  variant={activeTab === tab ? "outlinePrimary" : "outline"}
                  disabled={loadingActivityCoins}
                  size="lg"
                  className="px-4 py-2 capitalize"
                >
                  {tab === TopTraders.ALL_TIME ? "All Time" : tab}
                </Button>
              ))}
            </div>
          </div>

          <UserMultipleSelect
            buttonClassName="w-[220px]"
            users={traders}
            onSelectedUsers={handleSelectedUsers}
          />
        </div>
      </div>

      <TradeTopTradersFilterPopover
        methods={methods}
        filters={filters}
        onResetFilters={handleResetFilters}
        onApplyFilters={handleApplyFilters}
      />

      <TrendingCoinsTable<TradingTopTradersByTokenByVolume>
        loading={loadingActivityCoins}
        columns={columns}
        data={topTraders}
        columnSorting={columnSorting}
        setColumnSorting={(sorting) =>
          handleColumnSortingChange(sorting as SortingState[])
        }
        wrapperClassName={!topTraders.length ? "min-w-full" : "min-w-[1024px]"}
        pagination={pagination}
        pageOffset={pageOffset}
        columnsWidth={COLUMNS_WIDTH}
        refetchData={refetchWithUpdatedParams}
      />

      <TablePagination
        loading={loadingActivityCoins}
        pagination={pagination}
        onPaginate={onPaginate}
        itemsPerPage={itemsPerPage}
        totalCount={totalCount}
      />
    </div>
  );
};

export default TradeTopTradersTableWrapper;
